import type { Variant } from '../../types'

export interface ResolvedVariant {
  /**
   * The selected variant object if the product has variants.
   * `null` when the product has no variants at all (edge case — still renders
   * the page using product-level fields).
   */
  variant: Variant | null
  /** True when the caller provided a variant slug but it didn't match. */
  variantSlugMismatch: boolean
  /** True when the canonical URL (default variant) is being shown. */
  isCanonical: boolean
}

/**
 * Pure variant resolver — extracted so it can be unit-tested without mounting
 * any React tree. Given a variant list and an optional URL slug, return the
 * variant to show and whether we are on the canonical (default) URL.
 */
export function resolveVariant(
  variants: readonly Variant[],
  variantSlug: string | undefined,
): ResolvedVariant {
  if (variants.length === 0) {
    return { variant: null, variantSlugMismatch: false, isCanonical: variantSlug === undefined }
  }

  if (variantSlug !== undefined) {
    const fromUrl = variants.find((v) => v.slug === variantSlug)
    if (fromUrl) {
      return { variant: fromUrl, variantSlugMismatch: false, isCanonical: false }
    }
    // Asked-for slug doesn't match any variant.
    const fallback = variants.find((v) => v.is_default) ?? variants[0]
    return { variant: fallback, variantSlugMismatch: true, isCanonical: true }
  }

  // No variantSlug in URL → canonical URL → default variant.
  const defaultVariant = variants.find((v) => v.is_default) ?? variants[0]
  return { variant: defaultVariant, variantSlugMismatch: false, isCanonical: true }
}
